import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - rect.width / 2) / 50;
        const y = (e.clientY - rect.top - rect.height / 2) / 50;
        setMousePosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <section ref={heroRef} className="relative h-screen w-full overflow-hidden">
      {/* Parallax Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-110 transition-transform duration-200"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1578575437130-527eed3abbec?w=1920&q=80')`,
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px) scale(1.1)`,
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      {/* Animated Diagonal Overlay */}
      <div 
        className="absolute inset-0 transition-all duration-1000"
        style={{
          clipPath: isVisible ? 'polygon(65% 0, 100% 0, 100% 100%, 85% 100%)' : 'polygon(100% 0, 100% 0, 100% 100%, 100% 100%)',
        }}
      >
        <div className="w-full h-full bg-white/10"></div>
      </div>

      {/* White Diagonal Line */}
      <div 
        className={`absolute top-0 bottom-0 w-1 bg-white/80 transform transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          left: '65%',
          transform: 'rotate(15deg) translateX(-50%)',
          transformOrigin: 'top',
          height: '120%',
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto w-full">
          <div className="max-w-2xl">
            {/* Animated Title */}
            <h1 
              className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light italic text-[#3ECDA3] leading-tight mb-6 tracking-wide transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {t('hero.title').split(' ').map((word, index) => (
                <span 
                  key={index} 
                  className="inline-block mr-3"
                  style={{ 
                    transitionDelay: `${index * 100}ms`,
                    animation: isVisible ? `fadeInUp 0.6s ease forwards ${index * 0.1}s` : 'none'
                  }}
                >
                  {word}{index === 0 && <br />}
                </span>
              ))}
            </h1>

            {/* Subtitle */}
            <p 
              className={`text-white text-base sm:text-lg md:text-xl max-w-xl mb-8 font-light transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              {t('hero.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToContent}
        className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white z-10 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 animate-bounce' : 'opacity-0'
        }`}
      >
        <ChevronDown size={32} strokeWidth={1} />
      </button>
    </section>
  );
};

export default Hero;
